import { GoogleLogin, googleLogout } from "@react-oauth/google";
import { type ReactNode, useEffect, useState } from "react";

interface JwtClaims {
  email: string;
  email_verified: boolean;
  name?: string;
  exp: number;
}

const STORAGE_KEY = "monitor.idToken";

function decode(jwt: string): JwtClaims | null {
  try {
    const payload = jwt.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

function isAllowed(email: string): boolean {
  const raw = (import.meta.env.VITE_ALLOWED_EMAILS as string | undefined) ?? "";
  const list = raw
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return list.includes(email.toLowerCase());
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

  useEffect(() => {
    if (!token) return;
    const claims = decode(token);
    if (!claims || claims.exp * 1000 < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      setToken(null);
    }
  }, [token]);

  if (!token) {
    return (
      <main className="auth-screen">
        <h1>Monitor</h1>
        <p>Sign in with your Google account to continue.</p>
        <GoogleLogin
          onSuccess={(resp) => {
            if (resp.credential) {
              localStorage.setItem(STORAGE_KEY, resp.credential);
              setToken(resp.credential);
            }
          }}
          onError={() => {
            console.error("Google sign-in failed");
          }}
        />
      </main>
    );
  }

  const claims = decode(token);
  if (!claims || !claims.email_verified || !isAllowed(claims.email)) {
    return (
      <main className="auth-screen">
        <h1>Access denied</h1>
        <p>
          {claims?.email
            ? `${claims.email} is not on the Monitor allowlist.`
            : "Your account is not on the Monitor allowlist."}
        </p>
        <button
          type="button"
          onClick={() => {
            googleLogout();
            localStorage.removeItem(STORAGE_KEY);
            setToken(null);
          }}
        >
          Sign out
        </button>
      </main>
    );
  }

  return <>{children}</>;
}

export function getIdToken(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}
